import { Injectable } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { produce } from 'immer';
import { getPdfForm } from '@core/util/forms';
import { PDFFormType, PDFFormTypes } from '@models/matter-form-data';
import { MatterFormDataDispatch } from '@state/matter/form-data';
import { MatterFormDataState } from '@state/matter/form-data/form-data.state';
import { Subscription } from 'rxjs';
import * as actions from './selected-matter-forms.actions';
import { BaseFormMapper } from 'src/app/private/sale/documents/forms/mappers/base-form-mapper';
import { FormMapper } from 'src/app/private/sale/documents/forms/mappers/form-mapper';
import { environment } from 'src/environments/environment';
import { envTypes } from 'src/environments/env.types';
import { MatterActions } from '@state/matter';

export interface SelectedForm {
	type: PDFFormTypes;
	pdfFormType: PDFFormType;
	formDataId: string;
	url: string;
	name: string;
	mortgageId: string;
	mappings: { [closerFormFieldId: string]: string };
	mergedFormData: { [closerFormFieldId: string]: string };
	finalMergedData: { [closerFormFieldId: string]: string };
}

interface SelectedMatterFormsStateModel {
	selected: SelectedForm;
}

type Ctx = StateContext<SelectedMatterFormsStateModel>;

@State<SelectedMatterFormsStateModel>({
	name: 'selectedMatterForms',
	defaults: {
		selected: {
			type: null,
			pdfFormType: null,
			formDataId: null,
			url: '',
			name: '',
			mortgageId: null,
			mappings: {},
			mergedFormData: {},
			finalMergedData: {},
		},
	},
})
@UntilDestroy()
@Injectable()
export class SelectedMatterFormsState {
	private selectedFormDataSubscription: Subscription;
	private formMapper: BaseFormMapper<any>;

	@Selector([SelectedMatterFormsState.selected])
	static getSelectedForm(selected: SelectedForm): SelectedForm {
		return selected;
	}

	@Selector([SelectedMatterFormsState])
	static selected(state: SelectedMatterFormsStateModel): SelectedForm {
		return state.selected;
	}

	constructor(
		private FormMapper: FormMapper,
		private formDataDispatch: MatterFormDataDispatch,
		private store: Store,
	) {}

	@Action([actions.SelectForm, MatterActions.Get], { cancelUncompleted: true })
	selectForm(
		{ patchState, setState, getState }: Ctx,
		{ payload }: actions.SelectForm,
	) {
		this.selectedFormDataSubscription?.unsubscribe();

		if (payload?.formType) {
			const pdfForm = getPdfForm(payload.formType);
			this.formMapper = this.FormMapper.getCloserToFormMappingFile(
				payload.formType,
			);
			setState(
				produce((state) => {
					state.selected = {
						type: pdfForm?.type,
						pdfFormType: payload.formType,
						formDataId: payload.formId ?? payload.formType,
						url: `/assets/pdf/${pdfForm?.url}`,
						name: pdfForm?.name,
						mortgageId: payload?.mortgageId,
						mappings: {},
						mergedFormData: {},
						finalMergedData: {},
					};
				}),
			);

			this.selectedFormDataSubscription = this.store
				.select(
					MatterFormDataState.getMergedFormData(
						payload.formType,
						this.formMapper,
						payload.formId,
						payload.mortgageId,
					),
				)
				.subscribe((result) => {
					if (environment.env !== envTypes.PRODUCTION) {
						console.log('Merged form data', result);
					}

					patchState({
						selected: {
							...getState().selected,
							mappings: result.mappings,
							mergedFormData: result.formData,
							finalMergedData: result.finalMergedData,
						},
					} as Partial<SelectedMatterFormsStateModel>);
				});
		}
	}

	@Action(actions.SetSelectedFormField)
	setSelectedFormField(
		{ getState }: Ctx,
		{ payload }: actions.SetSelectedFormField,
	) {
		const selectedForm = getState().selected;
		if (selectedForm) {
			if (selectedForm.mappings) {
				const closerMappingField = Object.entries(selectedForm.mappings).find(
					([key, value]) => value === payload.field,
				)?.[0];

				const previousValue =
					selectedForm.mergedFormData?.[closerMappingField]?.toString() ?? '';
				const incomingValue = payload.value?.toString() ?? '';

				if (previousValue != incomingValue) {
					this.formDataDispatch.SetField(
						selectedForm.formDataId,
						selectedForm.pdfFormType,
						selectedForm.pdfFormType,
						selectedForm.url,
						closerMappingField,
						payload.value,
					);
				}
			} else {
				this.formDataDispatch.SetField(
					selectedForm.formDataId,
					selectedForm.pdfFormType,
					selectedForm.pdfFormType,
					selectedForm.url,
					payload.field,
					payload.value,
				);
			}
		}
	}
}
